import Vue from "vue";
import axios from "axios";
import { DEFAULT_STYLES, BASE_FORMAT } from "./default-styles";

// Estilos do documento compartilhados por todos os editores da tela.
// Carrega do backend (/api/admin/document-styles); enquanto ele nao existir
// ou falhar, usa os estilos padrao (somente leitura).
const ENDPOINT = "/api/admin/document-styles";

export const styleState = Vue.observable({
    styles: DEFAULT_STYLES.map((style) => ({ ...style })),
    loaded: false,
    loading: false,
    remote: false,
});

let pending = null;

export function loadStyles({ force = false } = {}) {
    if (pending && !force) return pending;
    if (styleState.loaded && !force) return Promise.resolve(styleState.styles);
    styleState.loading = true;
    pending = axios.get(ENDPOINT)
        .then((result) => {
            const list = (result.data && result.data.content) || [];
            if (Array.isArray(list) && list.length) {
                styleState.styles = list.map(normalize);
                styleState.remote = true;
            }
            return styleState.styles;
        })
        .catch(() => styleState.styles)
        .finally(() => {
            styleState.loaded = true;
            styleState.loading = false;
            pending = null;
        });
    return pending;
}

export async function saveStyle(style) {
    const payload = normalize(style);
    const result = payload.id ? await axios.put(`${ENDPOINT}/${payload.id}`, payload) : await axios.post(ENDPOINT, payload);
    await loadStyles({ force: true });
    return result.data && result.data.content;
}

// Mensagem de erro devolvida pela API (CustomException do backend).
export function errorMessage(error, fallback) {
    const content = error && error.response && error.response.data && error.response.data.content;
    return (content && content.error && content.error.message) || fallback;
}

export function activeStyles(type) {
    return styleState.styles.filter((style) => style.active !== false && (!type || style.type === type));
}

export function findStyle(key) {
    return styleState.styles.find((style) => style.key === key) || null;
}

// "Titulo do Parecer" -> "titulo-do-parecer" (unico na lista).
export function uniqueKey(name, ignoreId = null) {
    const base = String(name || "estilo").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()
        .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "estilo";
    let key = base;
    let index = 2;
    while (styleState.styles.some((style) => style.key === key && style.id !== ignoreId)) key = `${base}-${index++}`;
    return key;
}

function normalize(style) {
    return { ...BASE_FORMAT, active: true, ...style };
}

// Atributos de paragrafo (no) para um estilo. Em paragrafo comum, o que for
// igual ao formato base fica nulo para nao ir ao HTML; titulo grava tudo
// (o padrao de h1-h3 muda de um destino para outro).
export function paragraphAttributes(style) {
    const minimal = !style.element || style.element === "p";
    const differs = (field) => style[field] !== null && style[field] !== undefined && (!minimal || style[field] !== BASE_FORMAT[field]);
    const pt = (value) => `${value}pt`;
    const cm = (value) => (value ? `${value}cm` : null);
    return {
        dataStyle: style.key,
        fontFamily: differs("fontFamily") ? style.fontFamily : null,
        fontSize: differs("fontSize") ? style.fontSize : null,
        fontWeight: style.bold ? "bold" : null,
        fontStyle: style.italic ? "italic" : null,
        textTransform: style.uppercase ? "uppercase" : null,
        color: style.color || null,
        textAlign: style.align || BASE_FORMAT.align,
        lineHeight: differs("lineHeight") ? style.lineHeight : null,
        spaceBefore: differs("spaceBefore") ? pt(style.spaceBefore) : null,
        spaceAfter: differs("spaceAfter") ? pt(style.spaceAfter) : null,
        indentLeft: cm(Number(style.indentLeft) || 0),
        textIndent: cm(Number(style.firstLine) || 0),
    };
}

// Atributos de paragrafo zerados (paragrafo dentro da citacao, que tem
// formato fixo).
export function emptyParagraphAttributes() {
    const attrs = paragraphAttributes({ key: null, align: null });
    Object.keys(attrs).forEach((key) => { attrs[key] = null; });
    return attrs;
}

// Atributos da marca textStyle para um estilo de trecho.
export function characterAttributes(style) {
    return {
        charStyle: style.key,
        fontFamily: style.fontFamily || null,
        fontSize: style.fontSize || null,
        color: style.color || null,
        fontWeight: style.bold ? "bold" : null,
        fontStyle: style.italic ? "italic" : null,
        textTransform: style.uppercase ? "uppercase" : null,
    };
}

// CSS para pre-visualizar um estilo no painel e no formulario.
export function previewCss(style) {
    return {
        fontFamily: style.fontFamily || "\"Segoe UI\", \"Selawik\", sans-serif",
        fontSize: style.fontSize || "11pt",
        fontWeight: style.bold ? 700 : 400,
        fontStyle: style.italic ? "italic" : "normal",
        textTransform: style.uppercase ? "uppercase" : "none",
        color: style.color || "#111111",
        textAlign: style.align || "left",
    };
}
